// Light/dark theme store. The pre-paint inline script in app.html is the SSoT
// for the INITIAL class (it runs before first paint to avoid a flash); this
// store reads that resolved state, then owns runtime toggling + persistence.
//
// Default is DARK (the app's headline look) — only an explicit user choice of
// 'light' flips it; we deliberately do NOT auto-follow prefers-color-scheme, so
// behaviour is predictable and unchanged for everyone who never toggles.

export type Theme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'sayano:theme';

function resolveInitial(): Theme {
	// On the client the inline script (app.html) has already set the class before
	// this module loads, so the DOM is authoritative. On the server/prerender
	// there's no document — default to the dark shell.
	if (typeof document === 'undefined') return 'dark';
	return document.documentElement.classList.contains('dark') ? 'dark' : 'light';
}

let theme = $state<Theme>(resolveInitial());

export function getTheme(): Theme {
	return theme;
}

export function isDark(): boolean {
	return theme === 'dark';
}

/** Apply a theme: flips the `.dark` class (drives every token), mirrors
 *  `data-theme`, and persists the explicit choice so the inline script restores
 *  it on the next load. */
export function setTheme(next: Theme): void {
	theme = next;
	if (typeof document === 'undefined') return;
	const el = document.documentElement;
	el.classList.toggle('dark', next === 'dark');
	el.dataset.theme = next;
	try {
		localStorage.setItem(THEME_STORAGE_KEY, next);
	} catch {
		/* storage disabled — runtime toggle still works for this session */
	}
}

export function toggleTheme(): void {
	setTheme(theme === 'dark' ? 'light' : 'dark');
}
